import React from "react";
import { Scheduling } from "../../../../../models/schedule";
import { Asset } from "../../../../../models/asset";
import { Occurrence } from "../../../../../models/occurrence";
import useMediaQuery from "@mui/material/useMediaQuery";
import VehicleIcon from "../../../../VehicleIcon";
import { OccurrenceTypeLabelEnum } from "../../../../../enums/occurrenceType";

interface ISchedulingSummaryProps {
  scheduling?: Scheduling | null;
  asset?: Asset;
  occurrence?: Occurrence | null;
  persona?: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
}

const SchedulingSummary: React.FC<ISchedulingSummaryProps> = ({
  scheduling,
  asset,
  occurrence,
  persona
}) => {
  const isMobile = useMediaQuery("(max-width: 767px)");

  if (!scheduling && !asset && !occurrence) {
    return null;
  }

  const vehicleData = {
    name: asset?.name || scheduling?.asset?.name || occurrence?.vehicleName || "Veículo",
    licensePlate: asset?.licensePlate || scheduling?.asset?.licensePlate || occurrence?.vehicleLicensePlate || "N/A",
    chassis: asset?.chassis || scheduling?.asset?.chassis || occurrence?.vehicleChassis || "N/A",
    odometer: asset?.odometer || scheduling?.asset?.odometer || scheduling?.odometer || occurrence?.vehicleOdometer || "N/A",
    hourmeter: asset?.hourmeter || scheduling?.asset?.hourmeter || scheduling?.hourmeter || occurrence?.vehicleHourmeter || "N/A",
    model: asset?.model || scheduling?.asset?.model || occurrence?.vehicleModel || "",
    year: asset?.year || scheduling?.asset?.year || occurrence?.vehicleYear || null
  };

  const hasVehicleInfo = vehicleData.name !== "Veículo" || vehicleData.chassis !== "N/A" || vehicleData.licensePlate !== "N/A";

  return (
    <div className="margin-bottom-20">
      <div className="text-size-h5 font-bold margin-bottom-10">
        {scheduling ? 'Resumo do Agendamento' : 'Informações do Veículo'}
      </div>
      
      <div className="p-4 bg-lighter rounded margin-bottom-15">
        {scheduling && (
          <div className="margin-bottom-15">
            <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
              {scheduling?.scheduleNumber || "N/A"}
            </h6>
            <p className="text-color-status-resting text-size-14 m-0">
              Data: {scheduling?.date ? new Date(scheduling.date).toLocaleDateString('pt-BR') : "N/A"}
              {scheduling?.dealership?.name && (
                <>
                  <br />
                  Concessionária: {scheduling.dealership.name}
                </>
              )}
            </p>
          </div>
        )}

        {/* Informações da occurrence quando não há scheduling */}
        {!scheduling && occurrence && (
          <div className="margin-bottom-15">
            <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
              {occurrence.protocolNumber || "N/A"}
            </h6>
            <p className="text-color-status-resting text-size-14 m-0">
              Cliente: {occurrence.customer || "N/A"}
              {occurrence.dealershipName && (
                <>
                  <br />
                  Concessionária: {occurrence.dealershipName}
                </>
              )}
            </p>
          </div>
        )}

        {/* Informações do veículo usando dados consolidados */}
        {hasVehicleInfo && (
          <div className={`flex items-start gap-4 ${isMobile ? "flex-col gap-3 items-center" : ""}`}>
            <VehicleIcon 
              chassis={vehicleData.chassis} 
              size={80}
            />

            <div className="flex-1 padding-left-10">
              <p className="text-color-black text-size-16 font-medium m-0">
                {vehicleData.name} | Placa: {vehicleData.licensePlate}
              </p>
              <p className="text-color-status-resting text-size-14 m-0">
                Chassi - {vehicleData.chassis}
                <br />
                Odômetro: {vehicleData.odometer} km | Horímetro: {vehicleData.hourmeter}
                {vehicleData.model && (
                  <>
                    <br />
                    Modelo: {vehicleData.model}
                  </>
                )}
                {occurrence?.occurrenceType && (
                  <>
                    <br />
                    Tipo de ocorrência:{" "}
                    {OccurrenceTypeLabelEnum[occurrence.occurrenceType as keyof typeof OccurrenceTypeLabelEnum] ?? occurrence.occurrenceType ?? "Não informado"}
                  </>
                )}
              </p>
            </div>

            {scheduling?.date && (
              <div className="margin-bottom-15">
                <h6 className="text-size-16 text-color-black font-bold margin-bottom-5">
                  Data do Agendamento
                </h6>
                <p className="text-color-status-resting text-size-14 m-0">
                  {new Date(scheduling.date).toLocaleDateString('pt-BR')}
                </p>
              </div>
            )}

            {/* Tags de planos */}
            {((asset?.plans?.length ?? 0) > 0 || (scheduling?.asset?.plans?.length ?? 0) > 0) && (
              <div className="flex gap-6 margin-bottom-20">
                <span className="px-2 py-1 green-tag">RIO</span>
                <span className="px-2 py-1 green-tag">Em Garantia</span>
                {(asset?.volksTotalPlan || scheduling?.asset?.volksTotalPlan) && (
                  <span className="px-2 py-1 bg-white white-tag">
                    Volkstotal {asset?.volksTotalPlan || scheduling?.asset?.volksTotalPlan}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Campanhas de Campo */}
      {scheduling?.campaigns && scheduling.campaigns.length > 0 && (
        <div className="revision-itens margin-bottom-15">
          <h6 className="text-size-16 text-color-black font-bold margin-bottom-10">
            Campanhas de Campo
          </h6>
          <div className="p-4 bg-white border rounded">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              {scheduling.campaigns.map((campaign: any, index: number) => (
                <li key={index} className="padding-bottom-5">
                  <div className="text-bold">{campaign.name || campaign.number}</div>
                  {campaign.description && (
                    <div className="text-color-status-resting padding-left-10">
                      {campaign.description}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Serviços e Peças Agendados */}
      {((scheduling?.checkup?.services?.length ?? 0) > 0 || (scheduling?.checkup?.parts?.length ?? 0) > 0 || (scheduling?.otherServices?.length ?? 0) > 0) && (
        <div className="revision-itens">
          <h6 className="text-size-16 text-color-black font-bold margin-bottom-10">
            Serviços e Peças Agendados
          </h6>
          <div className="p-4 bg-white border rounded">
            <ul className="space-y-5 text-size-14 text-color-black m-0 padding-left-10">
              
              {/* Serviços do checkup */}
              {scheduling?.checkup?.services?.map((service, index) => (
                <div key={index} className="padding-bottom-10">
                  <p className="text-bold">{service.name}</p>
                  {service.items?.map((item, itemIndex) => (
                    <p key={itemIndex} className="padding-left-20">
                      - {item}
                    </p>
                  ))}
                </div>
              ))}

              {/* Outros serviços */}
              {(scheduling?.otherServices?.length ?? 0) > 0 && (
                <div className="padding-bottom-10">
                  <p className="text-bold">Outros Serviços</p>
                  {scheduling?.otherServices?.map((service, index) => (
                    <p key={index} className="padding-left-20">
                      - {service}
                    </p>
                  ))}
                </div>
              )}

              {/* Peças */}
              {(scheduling?.checkup?.parts?.length ?? 0) > 0 && (
                <div className="padding-bottom-10">
                  <p className="text-bold">Peças</p>
                  {scheduling?.checkup?.parts?.map((part, index) => (
                    <p key={index} className="padding-left-20 py-2">
                      {part.qtd}x {part.description}
                      {part.code && (
                        <span className="text-color-status-resting"> - Cód.: {part.code}</span>
                      )}
                    </p>
                  ))}
                </div>
              )}

              {/* Caso não tenha serviços/peças */}
              {!(scheduling?.checkup?.services?.length ?? 0) && 
               !(scheduling?.checkup?.parts?.length ?? 0) && 
               !(scheduling?.otherServices?.length ?? 0) && (
                <p className="text-color-status-resting">Nenhum serviço ou peça agendado</p>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Observações */}
      {(scheduling?.observation || scheduling?.comments) && (
        <div className="revision-itens margin-top-15">
          <h6 className="text-size-16 text-color-black font-bold margin-bottom-10">
            Observações
          </h6>
          <div className="p-4 bg-white border rounded">
            <p className="text-size-14 text-color-black m-0">
              {scheduling?.observation || scheduling?.comments || "-"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulingSummary;
